import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { useSiteMonitoring } from '@api/useSiteMonitoring'
import { useManualReading } from '@components/_SiteWide/TankDisplay/hooks/useManualReading'
import { useTankRundown } from '@components/_SiteWide/TankDisplay/hooks/useTankRundown'
import { LastReadingPopover } from '@components/_SiteWide/TankDisplay/LastReadingPopover'
import { ProjectedDropToggle } from '@components/_SiteWide/TankDisplay/ProjectedDropToggle'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { OrderFlagAlert } from '@components/OrderWizard/Panes/DisplayPane/DisplayComponents/OrderFlagAlert'
import { SiteDeliveriesGrid } from '@components/StoreMonitoring/StoreDetailsPage/AttendantView/SiteDeliveriesGrid'
import { StoreDetailsTankRundown } from '@components/StoreMonitoring/StoreDetailsPage/StoreDetailsTankRundown'
import { StoreTabView } from '@components/StoreMonitoring/StoreDetailsPage/TabView'
import { TankListCards } from '@components/StoreMonitoring/StoreDetailsPage/TankListCards/TankListCards'
import {
  addCommasToNumber,
  GraviButton,
  Horizontal,
  MaterialIcon,
  NothingMessage,
  Texto,
  useDeviceType,
  Vertical,
} from '@gravitate-js/excalibrr'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useUserSettings } from '@hooks/settings'
import cnf from '@utils/cell_formatter'
import { ReadableDifference } from '@utils/general'
import { Collapse, Descriptions, Drawer, Form, Modal, Popover, Splitter, Switch, Tooltip } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { getInitialMarkerState, TankGraphWidget } from '../../_SiteWide/TankDisplay/TankGraphWidget'
import { useSiteMonitoringContext } from '../SiteMonitoringContext'
import { EasyOrderMovementsDrawer } from './AttendantView/EasyOrderMovements'
import { useEasyOrderMovements } from './AttendantView/EasyOrderMovements/MovementStoreProvider'
import { StoreDetailProducts } from './StoreDetailProducts'
import { TankReadingModal } from './TankReadingModal'
import { Details } from '../StoreDashboard/mobile/Details'
import { onSiteArrowClick } from '../Util/general'

const { localStorage } = window
export function StoreDetail() {
  const { showModalWithStore, detailStoreIds } = useSiteMonitoringContext()
  const { storeId } = useParams()
  const [store, setDetailStore] = useState()
  const { scopes } = useSecurityContext()
  const navigate = useNavigate()
  const location = useLocation()
  const canCreateOrder = scopes?.create_order
  const canViewSite = scopes?.admin?.site_read
  const canViewOrders = scopes.scopes.includes('o.r') || scopes?.create_order
  const [_, setVisible] = useState(false)
  const [form] = Form.useForm()
  const { useSiteByIdQuery } = useSiteMonitoring()
  const { isMobile } = useDeviceType()

  useEffect(() => {
    const authorizedIds = scopes?.store_ids
    const isDashboard = storeId === 'dashboard' && authorizedIds.length > 0
    const isStoreAuthorized = authorizedIds.includes(storeId)

    if ((!isStoreAuthorized || isDashboard) && authorizedIds.length > 0) {
      localStorage.setItem('last_site_monitoring_page', authorizedIds[0])
      navigate(`/site_monitoring/${authorizedIds[0]}`)
    }
  }, [storeId, scopes])
  const { data: storeData, fetching, loading, isError } = useSiteByIdQuery(storeId)

  const { updateSetting } = useUserSettings()

  const { selectedTank, setSelectedTank, selectedManifold, setSelectedManifold, tankRundownData, setTankRundownData } =
    useTankRundown({
      store,
      shouldDefaultTankSelection: true,
    })
  const {
    tankReading,
    setTankReading,
    saveTankReading,
    closeTankModal,
    setShowingTankModal,
    showingTankModal,
    manualEntry,
    setManualEntry,
  } = useManualReading({
    form,
    setSelectedTank,
    setDetailStore,
    selectedTank,
    setTankRundownData,
    store,
  })

  const { value: rundownToggled, setValue: setRundownToggle } = useLocalStorage('show_projected_drops', true)
  const setToggle = (value) => {
    setRundownToggle(value)
    updateSetting('show_projected_drops', value)
  }

  const { value: inventoryChartExpanded, setValue: setICExpanded } = useLocalStorage(
    'SiteDetails::InventoryChartExpanded',
    true
  )

  const setInventoryChartExpanded = (value: boolean) => {
    setICExpanded(value)
    updateSetting('SiteDetails::InventoryChartExpanded', value)
  }

  const { value: tankPanelSize, setValue: setTankPanelSize } = useLocalStorage('SiteDetails::tankPanelSize', '12%')

  function handleHorizontalResize(sizes: number[]) {
    const tankPanelSize = sizes[0]
    setTankPanelSize(tankPanelSize.toString())
    updateSetting('SiteDetails::tankPanelSize', tankPanelSize.toString())
  }

  useEffect(() => {
    if (storeData) {
      setDetailStore(storeData)
      if (storeData?.monitoring_strategy === 'No Readings (Call-In)') {
        setInventoryChartExpanded(false)
      } else {
        setInventoryChartExpanded(true)
      }
    }
  }, [storeData])

  const { value: graphMarkers, setValue: settingGraphMarkers } = useLocalStorage(
    'tank_rundown_graph_markers',
    getInitialMarkerState()
  )
  const setGraphMarkers = async (value) => {
    settingGraphMarkers(value)
    await updateSetting('tank_rundown_graph_markers', value)
  }

  const toggleGraphMarker = (key) => setGraphMarkers({ ...graphMarkers, [key]: !graphMarkers[key] })

  const { openDrawer } = useEasyOrderMovements()

  const toggleChartExpand = () => setInventoryChartExpanded(!inventoryChartExpanded)

  const { useSiteDetailsTankReadingsQuery } = useSiteMonitoring()
  const filters = useMemo(
    () => ({
      store_number: store?.store_number,
      tank_id: selectedManifold?.manifold_id || selectedTank?.tank_id,
    }),
    [selectedTank, selectedManifold, store]
  )

  const {
    data: tankReadingData,
    isFetching: isFetchingTankReadings,
    refetch,
  } = useSiteDetailsTankReadingsQuery(filters)

  const saveTankReadingWrapper = async (values, callback) => {
    await saveTankReading(values, callback)
    await refetch()
  }

  if (isMobile) return <Details store={storeData} />
  if (fetching || loading || !store || !storeData) return <LoadingOutlined />

  if (isError)
    return (
      <Vertical flex={1} horizontalCenter verticalCenter>
        <NothingMessage title='Unable To Find Store' message='Unable to find store with that ID.' />
        <Vertical flex={1}>
          <GraviButton
            theme2
            icon={<LeftOutlined />}
            buttonText='Go Back Home'
            onClick={() => {
              const splitPath = location.pathname.split('/')
              if (splitPath[1]) {
                localStorage.removeItem('last_page_section')
                updateSetting('last_page_section', null)
              }
              if (splitPath[2]) {
                localStorage.setItem(`last_${splitPath[1]}_page`, 'dashboard')
                updateSetting(`last_${splitPath[1]}_page`, 'dashboard')
              }
              navigate('/')
            }}
          />
        </Vertical>
      </Vertical>
    )

  if (scopes.store_ids?.length === 1) {
    return (
      <>
        <Drawer
          // Temporarily disabled for costco. Will likely be re-enabled in the future.
          // visible={isVisible}
          placement='bottom'
          onClose={() => setVisible(false)}
          height='50vh'
          bodyStyle={{ display: 'flex', flexDirection: 'column' }}
        >
          <SelectedTankHeader
            tank={selectedTank}
            selectedManifold={selectedManifold}
            addTankReading
            setRundownToggle={setToggle}
            rundownToggled={rundownToggled}
            setShowingTankModal={setShowingTankModal}
            setManualEntry={setManualEntry}
            graphMarkers={graphMarkers}
            toggleGraphMarker={toggleGraphMarker}
          />
          <Horizontal className='bordered' flex={1}>
            <Vertical flex={4} style={{ overflow: 'auto' }}>
              <StoreDetailsTankRundown
                timezone={store.timezone}
                tankRundownData={tankRundownData}
                tank={selectedTank}
                selectedManifold={selectedManifold}
                showTitle
                addTankReading
                setShowingTankModal={setShowingTankModal}
                setManualEntry={setManualEntry}
                rundownToggled={rundownToggled}
                setRundownToggle={setToggle}
                graphMarkers={graphMarkers}
              />
            </Vertical>
            <Vertical flex={0.5} style={{ minHeight: '100%', height: 'auto' }} className='bg-2'>
              <TankStats
                tankRundownData={tankRundownData}
                timezone={store.timezone}
                tank={selectedTank}
                selectedManifold={selectedManifold}
                rundownToggled={rundownToggled}
              />
            </Vertical>
          </Horizontal>
        </Drawer>
        <TankReadingModal
          form={form}
          storeNumber={store.store_number}
          tankId={selectedTank?.tank_id}
          tankReading={tankReading}
          showingTankModal={showingTankModal}
          manualEntry={manualEntry}
          setTankReading={setTankReading}
          closeTankModal={closeTankModal}
          saveTankReading={saveTankReadingWrapper}
          setManualEntry={setManualEntry}
        />
        <Vertical>
          <Vertical className='horizontal-tank-select-container mt-2' flex='0 1 auto' style={{ height: 'auto' }}>
            <TankListCards
              setIsVisible={setVisible}
              selectedManifold={selectedManifold}
              setSelectedManifold={setSelectedManifold}
              store={store}
              selectedTank={selectedTank}
              setSelectedTank={setSelectedTank}
              visibleTankIds={store.grouped_tanks.flatMap((group) => group.tanks.map((tank) => tank.tank_id))}
            />
          </Vertical>
          <div>
            <EasyOrderMovementsDrawer />
          </div>
          <Vertical flex='1 1 auto'>
            <SiteDeliveriesGrid store={store} showModal={showModalWithStore} />
          </Vertical>
        </Vertical>
      </>
    )
  }

  return (
    <Vertical>
      <TankReadingModal
        form={form}
        storeNumber={store.store_number}
        tankId={selectedTank?.tank_id}
        tankReading={tankReading}
        showingTankModal={showingTankModal}
        manualEntry={manualEntry}
        setTankReading={setTankReading}
        closeTankModal={closeTankModal}
        saveTankReading={saveTankReadingWrapper}
        setManualEntry={setManualEntry}
      />
      <Vertical flex={1}>
        <Horizontal
          className='px-3'
          border='all'
          borderRadius='6px 0 0 0'
          verticalCenter
          justifyContent='space-between'
        >
          <GraviButton
            color='primary'
            appearance='link'
            icon={<ArrowLeftOutlined />}
            style={{ padding: 0 }}
            onClick={() => navigate('/site_monitoring/dashboard')}
            buttonText={<Texto weight='bold'>Back to All Sites</Texto>}
          />
        </Horizontal>
        <Horizontal flex={1} className='h-100'>
          <Splitter onResizeEnd={handleHorizontalResize}>
            <Splitter.Panel defaultSize={tankPanelSize}>
              <Vertical flex={1} className='bg-1 vertical-tank-select-container' style={{ overflowY: 'scroll' }}>
                {store.notes && (
                  <Collapse defaultActiveKey={['1']}>
                    <Collapse.Panel header='Site Notes' key='1'>
                      {store.notes}
                    </Collapse.Panel>
                  </Collapse>
                )}
                <div>
                  <StoreDetailProducts
                    rundownToggled={rundownToggled}
                    selectedManifold={selectedManifold}
                    setSelectedManifold={setSelectedManifold}
                    store={store}
                    selectedTank={selectedTank}
                    setSelectedTank={setSelectedTank}
                    visibleTankIds={store.grouped_tanks.flatMap((group) => group.tanks.map((tank) => tank.tank_id))}
                  />
                </div>
              </Vertical>
            </Splitter.Panel>
            <Splitter.Panel min='50%'>
              <Vertical flex={3} className='tank-details-container'>
                <Vertical className='flex-1 pt-0 bg-1'>
                  <SiteHeader
                    store={store}
                    showModalWithStore={showModalWithStore}
                    canCreateOrder={canCreateOrder}
                    canViewSite={canViewSite}
                    onClickForwardBack={(back) => onSiteArrowClick(back, detailStoreIds, storeId, navigate)}
                    detailStoreIds={detailStoreIds}
                  />
                  <Splitter layout='vertical'>
                    <Splitter.Panel min='20%' defaultSize='40%' size={inventoryChartExpanded ? undefined : 0}>
                      <Vertical height='100%'>
                        <SelectedTankHeader
                          tank={selectedTank}
                          selectedManifold={selectedManifold}
                          addTankReading
                          setRundownToggle={setToggle}
                          rundownToggled={rundownToggled}
                          setShowingTankModal={setShowingTankModal}
                          setManualEntry={setManualEntry}
                          graphMarkers={graphMarkers}
                          toggleGraphMarker={toggleGraphMarker}
                        />
                        <Horizontal className='bordered' flex={1}>
                          <Vertical flex={5} height='100%'>
                            <StoreDetailsTankRundown
                              timezone={store.timezone}
                              tankRundownData={tankRundownData}
                              tank={selectedTank}
                              selectedManifold={selectedManifold}
                              showTitle
                              addTankReading
                              setShowingTankModal={setShowingTankModal}
                              setManualEntry={setManualEntry}
                              rundownToggled={rundownToggled}
                              setRundownToggle={setToggle}
                              graphMarkers={graphMarkers}
                            />
                          </Vertical>
                          <Vertical flex={1} className='bg-2'>
                            <TankStats
                              tankRundownData={tankRundownData}
                              timezone={store.timezone}
                              tank={selectedTank}
                              selectedManifold={selectedManifold}
                              rundownToggled={rundownToggled}
                            />
                          </Vertical>
                        </Horizontal>
                      </Vertical>
                    </Splitter.Panel>
                    <Splitter.Panel min='30%'>
                      <Vertical>
                        {canViewOrders && (
                          <StoreTabView
                            store={store}
                            showModalWithStore={showModalWithStore}
                            selectedManifold={selectedManifold}
                            selectedTank={selectedTank}
                            rundownToggled={rundownToggled}
                            inventoryChartExpanded={inventoryChartExpanded}
                            toggleChartExpand={toggleChartExpand}
                            tankReadingData={tankReadingData}
                            isFetchingTankReadings={isFetchingTankReadings}
                          />
                        )}
                      </Vertical>
                    </Splitter.Panel>
                  </Splitter>
                </Vertical>
              </Vertical>
            </Splitter.Panel>
          </Splitter>
        </Horizontal>
      </Vertical>
    </Vertical>
  )
}

function TankStats({ tank, tankRundownData, selectedManifold, timezone, rundownToggled }) {
  const cardData = useMemo(() => {
    const data = tank || selectedManifold
    if (!data) return []

    const cards = [
      { title: 'Estimated Current Volume', value: addCommasToNumber(data.current_volume, 0) },
      {
        title: 'Runout Time',
        value: ReadableDifference(rundownToggled ? data.run_out_date_with_drops : data.run_out_date, undefined, true),
      },
      { title: 'Current Ullage', value: addCommasToNumber(data.storage_max - data.current_volume, 0) },
    ]
    return cards
  }, [tank, selectedManifold, rundownToggled])

  return (
    <>
      {cardData.map((card, index) => {
        return (
          <Vertical flex={1} className='border-bottom pl-3 pt-2 pb-2' verticalCenter>
            <Texto appearance='medium'>{card.title}</Texto>
            <Horizontal verticalCenter>
              <Texto category='h4' className='tank-metric'>
                {card.value}
              </Texto>
              {index === 0 && (
                <Popover
                  content={<LastReadingPopover timezone={timezone} tankRundownData={tankRundownData} />}
                  title={<Texto category='heading-small'>Last Reading</Texto>}
                  trigger='hover'
                >
                  <div>
                    <Texto className='ml-2'>
                      <InfoCircleOutlined className='current-volume info-icon' />
                    </Texto>
                  </div>
                </Popover>
              )}
            </Horizontal>
          </Vertical>
        )
      })}
    </>
  )
}

function SelectedTankHeader({
  tank,
  selectedManifold,
  addTankReading,
  rundownToggled,
  setRundownToggle,
  setShowingTankModal,
  setManualEntry,
  graphMarkers,
  toggleGraphMarker,
}) {
  const { scopes } = useSecurityContext()
  const [showingTankDetails, setShowingTankDetails] = useState(false)
  const canEnterManualReading = scopes.site_monitoring?.manual_reading && !selectedManifold
  const isCarrier = scopes?.is_carrier
  const showTankFlags = tank?.flags && tank.flags.length > 0
  const descriptionData = useMemo(() => {
    const data = tank || selectedManifold
    if (!data) return []
    return [
      { title: 'Estimated Current Volume', value: cnf.reg(data.current_volume) },
      { title: 'Target Minimum', value: cnf.reg(data.model_target_min) },
      { title: 'Target Maximum', value: cnf.reg(data.model_target_max) },
      { title: 'Max Volume', value: cnf.reg(data.storage_max) },
      { title: 'Tank Size', value: cnf.reg(data.tank_size) },
      {
        title: 'Runout Time',
        value: ReadableDifference(data.run_out_date, undefined, true),
      },
    ]
  }, [tank, selectedManifold])
  return (
    <>
      <Modal open={showingTankDetails} footer={null} onCancel={() => setShowingTankDetails(false)}>
        <Descriptions title='Tank Details' layout='vertical' bordered>
          {descriptionData.map((desc) => (
            <Descriptions.Item key={desc.value} label={desc.title}>
              {desc.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </Modal>
      <Horizontal border='all' verticalCenter className='px-3 py-2'>
        <Texto category='p2' className='pr-2' weight={600}>
          Selected Tank:
        </Texto>

        <Texto category='h5' weight={600} textTransform='uppercase'>
          <a onClick={() => setShowingTankDetails(true)}>
            {selectedManifold ? selectedManifold?.manifold_id : tank?.product}
          </a>
        </Texto>
        <Texto category='label' className='pl-1 pt-1'>{`(TANK ${
          selectedManifold ? JSON.stringify(selectedManifold.tank_ids) : tank?.tank_id
        })`}</Texto>
        {showTankFlags && !isCarrier && <OrderFlagAlert showLabel={false} flags={tank?.flags} />}
        <Horizontal gap='0.5rem' flex={1} justifyContent='flex-end' alignItems='center' className='py-1'>
          <ProjectedDropToggle
            value={rundownToggled}
            onChange={() => {
              setRundownToggle(!rundownToggled)
            }}
          />
          <TankGraphWidget graphMarkers={graphMarkers} onMarkerChange={toggleGraphMarker} />
          {canEnterManualReading && addTankReading && (
            <GraviButton
              theme2
              appearance='outline'
              buttonText='Add Reading'
              icon={<PlusOutlined />}
              onClick={() => {
                setShowingTankModal(true)
                setManualEntry(false)
              }}
            />
          )}
        </Horizontal>
      </Horizontal>
    </>
  )
}

function SiteHeader({ showModalWithStore, store, canCreateOrder, canViewSite, onClickForwardBack, detailStoreIds }) {
  const navigate = useNavigate()
  const index = detailStoreIds.findIndex((item) => item.id === store.id)
  return (
    <Horizontal style={{ backgroundColor: 'var(--bg-2)' }} width='100%' border='all'>
      <Horizontal className='px-3 py-1' verticalCenter width='100%' justifyContent='space-between'>
        <Horizontal gap={8} className='py-2' verticalCenter>
          <Tooltip title={index > 0 ? `Previous Site:  #${detailStoreIds[index - 1]?.number}` : null}>
            <GraviButton
              size='small'
              disabled={index < 1}
              onClick={() => onClickForwardBack(true)}
              icon={<MaterialIcon className='pt-1' icon='keyboard_arrow_left' size={18} />}
            />
          </Tooltip>

          <Texto category='h5' weight='600'>
            {store?.store_number} - {store?.name}
          </Texto>

          <Tooltip
            title={detailStoreIds?.length <= index + 1 ? null : `Next Site: #${detailStoreIds[index + 1]?.number}`}
          >
            <GraviButton
              size='small'
              disabled={detailStoreIds?.length <= index + 1}
              onClick={() => onClickForwardBack(false)}
              icon={<MaterialIcon className='pt-1' icon='keyboard_arrow_right' size={18} />}
            />
          </Tooltip>
        </Horizontal>
        <Horizontal gap={12}>
          <Horizontal verticalCenter>
            <MaterialIcon icon='pin_drop' />
            <Texto className='pl-1'>{[store?.address, store?.city, store?.state].filter(Boolean).join(', ')}</Texto>
          </Horizontal>
          <Horizontal verticalCenter>
            {store?.phone_number && (
              <>
                <MaterialIcon icon='call' />
                <Texto className='pl-1'>{store.phone_number}</Texto>
              </>
            )}
          </Horizontal>
          {store?.hours && (
            <Horizontal verticalCenter>
              <ClockCircleOutlined />
              <Texto className='pl-1' category='p2'>
                {store?.hours}
              </Texto>
            </Horizontal>
          )}
        </Horizontal>
      </Horizontal>
      <Horizontal className='mr-2' gap={8} verticalCenter style={{ flexShrink: 0 }}>
        {canViewSite && (
          <Tooltip title='View Site Info'>
            <GraviButton
              icon={<MaterialIcon icon='table_eye' size={16} />}
              className='ml-3'
              onClick={() => navigate(`/admin/sites?store_number=${store.store_number}`)}
            />
          </Tooltip>
        )}
        {canCreateOrder && (
          <GraviButton
            theme2
            buttonText='Create Order'
            icon={<PlusCircleOutlined />}
            onClick={() => showModalWithStore(store)}
          />
        )}
      </Horizontal>
    </Horizontal>
  )
}
